import axios from "axios";

/** Import-one: Habitaclia ~1–2 min; Idealista con Playwright puede superar 6 min. */
const LOCAL_API = "http://localhost:4000/api";
const DEFAULT_IMPORT_TIMEOUT_MS = 360_000;
export const IDEALISTA_IMPORT_TIMEOUT_MS = 600_000;
export const PISOS_IMPORT_TIMEOUT_MS = 480_000;

/** Mismo origen en producción (Vercel rewrite → Railway); evita CORS y cortes cross-origin. */
export function portalImportApiBase() {
  return window.location.hostname === "localhost" ? LOCAL_API : `${window.location.origin}/api`;
}

function authHeaders() {
  const token = localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function esErrorGateway(e) {
  const st = e.response?.status;
  const raw = String(e.response?.data?.error || e.response?.data?.mensaje || e.message || '');
  return st === 502 || st === 503 || st === 504 || /502|503|504|upstream error/i.test(raw);
}

async function axiosConReintento(requestFn, { intentos = 4, pausaMs = 2000 } = {}) {
  let lastErr;
  for (let i = 0; i < intentos; i++) {
    try {
      return await requestFn();
    } catch (e) {
      lastErr = e;
      if (!esErrorGateway(e) || i === intentos - 1) throw e;
      await sleep(pausaMs * (i + 1));
    }
  }
  throw lastErr;
}

export async function portalImportOne(path, body, { timeoutMs = DEFAULT_IMPORT_TIMEOUT_MS } = {}) {
  const { data } = await axios.post(`${portalImportApiBase()}${path}`, body, {
    timeout: timeoutMs,
    headers: authHeaders(),
  });
  return data;
}

/**
 * Idealista: POST async (respuesta rápida) + polling GET hasta done/error.
 * Evita mantener una conexión HTTP abierta 10 min (Railway edge / navegador / CORS).
 */
export async function portalImportIdealistaAsync(body, { timeoutMs = IDEALISTA_IMPORT_TIMEOUT_MS, pollMs = 2500 } = {}) {
  const base = portalImportApiBase();
  const headers = authHeaders();
  const { data: started } = await axiosConReintento(() => axios.post(`${base}/portal-v2/idealista/import-one-async`, body, {
    timeout: 45_000,
    headers,
  }));
  const jobId = started?.jobId;
  if (!jobId) throw new Error("El servidor no devolvió jobId para la importación.");

  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    await sleep(pollMs);
    const { data } = await axiosConReintento(() => axios.get(`${base}/portal-v2/idealista/import-one/job/${jobId}`, {
      timeout: 30_000,
      headers,
    }));
    if (data?.status === "running") continue;
    if (data?.status === "error") throw new Error(data.error || "Error al importar Idealista");
    if (data?.status === "done") {
      const { status, ...result } = data;
      return result;
    }
    return data;
  }
  const err = new Error("La importación superó el tiempo máximo (10 min en Idealista). Reintenta.");
  err.code = "ECONNABORTED";
  throw err;
}

/** Pisos.com: ScraperAPI premium/ultra puede superar el timeout del proxy si la petición es síncrona. */
export async function portalImportPisosAsync(body, { timeoutMs = PISOS_IMPORT_TIMEOUT_MS, pollMs = 2500 } = {}) {
  const base = portalImportApiBase();
  const headers = authHeaders();
  const { data: started } = await axiosConReintento(() => axios.post(`${base}/portal-v2/pisos/import-one-async`, body, {
    timeout: 90_000,
    headers,
  }));
  const jobId = started?.jobId;
  if (!jobId) throw new Error('El servidor no devolvió jobId para la importación.');

  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    await sleep(pollMs);
    const { data } = await axiosConReintento(() => axios.get(`${base}/portal-v2/pisos/import-one/job/${jobId}`, {
      timeout: 30_000,
      headers,
    }));
    if (data?.status === 'running') continue;
    if (data?.status === 'error') throw new Error(data.error || 'Error al importar Pisos.com');
    if (data?.status === 'done') {
      const { status, ...result } = data;
      return result;
    }
    return data;
  }
  const err = new Error('La importación superó el tiempo máximo (8 min en Pisos.com). Reintenta.');
  err.code = 'ECONNABORTED';
  throw err;
}

export function mensajeErrorPortalImport(e) {
  const st = e.response?.status;
  const raw = e.response?.data?.error || e.response?.data?.mensaje || e.message || "Error al importar";
  const codigo = e.response?.data?.codigo;
  if (st === 503 && codigo === "SCRAPING_DISABLED") {
    return "Scraping desactivado en el servidor. Revisa IMPORTACIONES_AUTOMATICAS o SCRAPING_MANUAL_PERMITIDO en Railway.";
  }
  if (st === 503 && /arrancando|boot/i.test(String(raw))) {
    return "El backend está arrancando. Espera 10–20 s y vuelve a importar.";
  }
  if (st === 502 || st === 503 || st === 504 || /502|503|504|upstream error/i.test(String(raw))) {
    return "El servidor no respondió a tiempo (503/502). Espera unos segundos y pulsa Importar de nuevo.";
  }
  if (e.code === "ECONNABORTED") {
    return e.message || "La importación superó el tiempo máximo. Reintenta en unos segundos.";
  }
  if (!e.response && /^network error$/i.test(String(e.message || ""))) {
    return "Error de red al contactar el backend. Comprueba conexión; si persiste, abre la consola (F12) o reintenta en unos segundos.";
  }
  return raw;
}
